import { Component, OnDestroy, OnInit, signal, inject } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { LicenseQuantityInputComponent } from '../license-quantity-input/license-quantity-input.component';
import { SubscriptionPlanCardComponent } from '../subscription-plan-card/subscription-plan-card.component';
import { LanguageService } from '../../../../../core/services/language.service';
import { HelpCardComponent } from '../help-card/help-card.component';
import { ProductDataService } from '../../services/product-data.service';
import { ProductService } from '../../services/product.service';
import { PaymentDataService } from '../../../payment/services/payment-data.service';
import { Store } from '@ngxs/store';
import { CurrentUserState } from '../../../../../core/store/current-user/current-user.state';
import { CustomPortalError } from '../../../../../core/errors/models/CustomPortalError';
import { SnackbarService } from '../../../../../core/services/snackbar.service';
import { TranslocoPipe } from '@jsverse/transloco';
import { environment } from '../../../../../../environments/environment';

interface TypePlan {
  planTranslationKey: string;
  preselected: boolean;
  isTrialing: boolean;
  loading: boolean;
  externalPriceId: string | null;
  unitPrice: number;
  totalPriceWithoutTaxAndDiscount: number;
  selectedTopicAreaIds: string[];
}

@Component({
  selector: 'app-product-selection-dialog',
  imports: [
    LicenseQuantityInputComponent,
    SubscriptionPlanCardComponent,
    MatIcon,
    HelpCardComponent,
    TranslocoPipe,
  ],
  templateUrl: './product-selection-dialog.component.html',
})
export class ProductSelectionDialogComponent implements OnInit, OnDestroy {
  protected readonly productDataService = inject(ProductDataService);
  private readonly paymentDataService = inject(PaymentDataService);
  private readonly productService = inject(ProductService);
  private readonly languageService = inject(LanguageService);
  private readonly store = inject(Store);
  private readonly snackbarService = inject(SnackbarService);
  readonly dialogRef =
    inject<MatDialogRef<ProductSelectionDialogComponent>>(MatDialogRef);

  private readonly destroy$: Subject<void> = new Subject();

  readonly headline = signal<string>('');

  plans: TypePlan[] = [
    {
      planTranslationKey: 'allInOne',
      preselected: true,
      loading: false,
      isTrialing: this.productDataService.isTrialingAllInOne(),
      externalPriceId: null,
      unitPrice: 0,
      totalPriceWithoutTaxAndDiscount: 0,
      selectedTopicAreaIds: this.setSelectedTopicAreasForPlan('allInOne'),
    },
    {
      planTranslationKey: 'flex',
      preselected: false,
      loading: false,
      isTrialing: !this.productDataService.isTrialingAllInOne(),
      externalPriceId: null,
      unitPrice: 0,
      totalPriceWithoutTaxAndDiscount: 0,
      selectedTopicAreaIds: this.setSelectedTopicAreasForPlan('flex'),
    },
  ];

  get isDowngradingTopicAreas(): boolean {
    return !this.plans.every((p) =>
      this.productDataService
        .trialingTopicAreaIds()
        .every((id) => p.selectedTopicAreaIds.includes(id)),
    );
  }

  ngOnInit() {
    this.fetchProductDetails();
    this.headline.set(this.getHeadline());
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCloseBtnClick() {
    this.closeDialog(null);
  }

  closeDialog(externalPriceId: string | null) {
    if (externalPriceId) {
      this.productDataService.priceId.set(externalPriceId);
      void this.productDataService.getProductDetails();
    }
    this.dialogRef.close();
  }

  getHeadline(): string {
    const plan = this.plans.find((p) => p.isTrialing)?.planTranslationKey;
    const planTranslated = this.languageService.translateOnRuntime(
      `product.dialog.plans.${plan}.name`,
    );
    const { currentPeriodEndsAt } = this.store.selectSnapshot(
      CurrentUserState.subscriptionBillingPeriod,
    );

    const now = new Date();

    let headline = '';

    if (currentPeriodEndsAt && now < new Date(currentPeriodEndsAt)) {
      const trialEnd = new Date(currentPeriodEndsAt);
      now.setHours(0, 0, 0, 0);
      trialEnd.setHours(0, 0, 0, 0);
      const daysRemaining = Math.floor(
        (trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
      );
      if (daysRemaining > 0) {
        headline = this.languageService.translateOnRuntime(
          'product.dialog.headlines.expiresIn',
          {
            plan: planTranslated,
            expiresIn:
              daysRemaining === 1
                ? this.languageService.translateOnRuntime(
                    'product.dialog.headlines.day',
                  )
                : this.languageService.translateOnRuntime(
                    'product.dialog.headlines.days',
                    { n: daysRemaining },
                  ),
          },
        );
      }
      if (daysRemaining === 0) {
        headline = this.languageService.translateOnRuntime(
          'product.dialog.headlines.expiresToday',
          {
            plan: planTranslated,
          },
        );
      }
    } else {
      headline = this.languageService.translateOnRuntime(
        'product.dialog.headlines.expired',
        {
          plan: planTranslated,
        },
      );
    }
    return headline;
  }

  fetchProductDetails() {
    for (const plan of this.plans) {
      this.fetchProductDetailsForPlan(plan);
    }
  }

  fetchProductDetailsForPlan(plan: TypePlan) {
    const selectedCountry = this.paymentDataService.selectedCountry();
    const quantity = this.productDataService.quantity();
    if (!quantity) {
      return;
    }
    plan.loading = true;
    firstValueFrom(
      this.productService.getProductDetails({
        selectedCountry,
        quantity,
        selectedTopicAreaIds: plan.selectedTopicAreaIds,
      }),
    )
      .then(({ product }) => {
        if (
          quantity === this.productDataService.quantity() &&
          product.topicAreas.length === plan.selectedTopicAreaIds.length &&
          product.topicAreas.every((topicArea) =>
            plan.selectedTopicAreaIds.includes(topicArea.id),
          )
        ) {
          const unitPrice = product.price.unitAmountExcludingTaxAndDiscount;
          plan.unitPrice = this.productDataService.toEuros(unitPrice);
          plan.totalPriceWithoutTaxAndDiscount =
            this.productDataService.toEuros(unitPrice * quantity);
          plan.externalPriceId = product.externalPriceId;
          plan.loading = false;
        }
      })
      .catch((error: CustomPortalError) => {
        this.snackbarService.error(error.errorType);
        plan.loading = false;
      });
  }

  onSupportButtonClick() {
    window.open(
      `mailto:${environment.general.hvsSupportContactEmail}`,
      '_blank',
    );
  }

  onSalesButtonClick() {
    window.open(`mailto:kontakt@is-fox.com`, '_blank');
  }

  setSelectedTopicAreasForPlan(plan: 'allInOne' | 'flex') {
    if (plan === 'allInOne') {
      return this.productDataService.topicAreas().map((t) => t.id);
    } else {
      if (this.productDataService.isTrialingAllInOne()) {
        return [this.productDataService.topicAreas()[0]?.id];
      } else {
        return [...this.productDataService.trialingTopicAreaIds()];
      }
    }
  }
}
